import React from 'react';
import {
  Stack,
  Button,
  ButtonGroup,
  Input,
  Select,
  Text,
  Modal,
  Shelf,
  Toggle,
} from '@chaser/components';
import CardSection from '../../components/CardSection/index';
import { Form, Formik } from 'formik';
import LabeledFieldWithErrorLabel from '../../components/LabeledFieldWithErrorLabel/index';
import LabeledFieldWithError from '../../components/LabeledFieldWithError/index';
import * as Yup from 'yup';
import NotificationService from '../../services/notification-service';
import useModal from '../../hooks/useModal';
import usePush from '../../hooks/usePush';
import useUserInfo from '../../hooks/useUserInfo';
import { useStorageState } from '../../hooks/useGlobalState';

// eslint-disable-next-line react/prop-types
const NotificationModal = ({ open, close, options, type, onClosed }) => {
  const { execute, loading } = usePush(NotificationService.sendAdminNotification, {
    errorToast: true,
    successToast: 'Notifications sent successfully!',
  });

  return (
    <Modal open={open} toggle={close} title="Send notification" size="medium" onClosed={onClosed}>
      <Modal.Body>
        <Shelf gap="small">
          <Text>
            Are you sure you want to send the notification about <strong>{type}</strong>?
          </Text>
        </Shelf>
      </Modal.Body>
      <Modal.Footer>
        <Shelf justifyContent="flex-end" gap="xsmall">
          <Button
            data-testid="sendnotif-button"
            data-trackingid="sendnotif-button"
            loading={loading}
            onClick={async () => {
              await execute(type, options);
              close(true);
            }}
          >
            Send notification
          </Button>
          <Button onClick={() => close(true)} appearance="secondary">
            Cancel
          </Button>
        </Shelf>
      </Modal.Footer>
    </Modal>
  );
};

const Settings = () => {
  const [{ features = false }, setState] = useStorageState('admin-settings');

  const showModal = useModal();
  const { isAdmin } = useUserInfo();

  function showAdminFeatures() {
    if (!isAdmin) return;

    setState({ features: !features });
  }

  return (
    <Stack>
      <CardSection title="Toggles">
        <Stack>
          <Text fontColor="Mid Neutral 1">
            Show features only available to admins, these will highlighted by a red border
          </Text>
          <Shelf>
            <Toggle
              toggleColor={features ? 'Green' : 'Red'}
              handleToggle={showAdminFeatures}
              toggleOn={features}
            />
            <Text>Show admin only features</Text>
          </Shelf>
        </Stack>
      </CardSection>
      <CardSection title="Collection notifications">
        <Stack gap="large">
          <Formik
            initialValues={{
              minInvoiceValue: 250,
              minOverdueDays: 90,
              collectableInvoicesPerCustomer: 3,
              currency: 'GBP',
            }}
            validationSchema={Yup.object().shape({
              minInvoiceValue: Yup.number()
                .required()
                .min(0),
              minOverdueDays: Yup.number()
                .required()
                .min(0),
              collectableInvoicesPerCustomer: Yup.number()
                .required()
                .min(1),
              currency: Yup.string().required(),
            })}
            validateOnBlur={false}
            onSubmit={async ({
              minInvoiceValue,
              minOverdueDays,
              collectableInvoicesPerCustomer,
              currency,
            }) => {
              showModal(
                <NotificationModal
                  type="collections"
                  options={{
                    minInvoiceValue: parseInt(minInvoiceValue),
                    minOverdueDays: parseInt(minOverdueDays),
                    collectableInvoicesPerCustomer: parseInt(collectableInvoicesPerCustomer),
                    currency,
                  }}
                />,
              );
            }}
          >
            {form => (
              <Form>
                <Stack gap="large">
                  <Stack gap="small">
                    <LabeledFieldWithErrorLabel
                      label="Minimum invoice value"
                      name="minInvoiceValue"
                      as={Input}
                      placeholder="250"
                    />
                    <LabeledFieldWithErrorLabel
                      label="Minimum overdue days"
                      name="minOverdueDays"
                      as={Input}
                      type="text"
                      placeholder="90"
                    />
                    <LabeledFieldWithErrorLabel
                      label="Number of collectable invoices per customer"
                      name="collectableInvoicesPerCustomer"
                      as={Input}
                      type="text"
                      placeholder="3"
                    />
                    <LabeledFieldWithError
                      label="Currency"
                      name="currency"
                      type="text"
                      as={Select}
                      width={130}
                      disabled
                    >
                      <option key={0} value="GBP">
                        GBP
                      </option>
                    </LabeledFieldWithError>
                  </Stack>
                  <ButtonGroup>
                    <Button
                      disabled={!form.isValid}
                      data-trackingid="notif-modal-btn"
                      data-testid="notif-modal-btn"
                      type="submit"
                    >
                      Send notification
                    </Button>
                  </ButtonGroup>
                </Stack>
              </Form>
            )}
          </Formik>
        </Stack>
      </CardSection>
    </Stack>
  );
};

export default Settings;
